/**
 * PDM BYOB Collection Component
 *
 * Manages the Build Your Own Bundle collection functionality including:
 * - Product carousels (Swiper)
 * - Tab navigation with active state
 * - Selected items management
 * - Checkout bar interactions
 */
/** Shared state for float message auto-hide timer (initFloatMessage). */
let countTimeout, timerGlobal, idInterval;
(function () {
    'use strict';

    /**
     * Line item property contract for the BYOB Shopify Discount Function.
     * Only items added via handleAddToCart() include this property.
     * @see docs/byob-discount-function-spec.md
     */
    const BYOB_LINE_PROPERTY_KEY = '_byob';
    const BYOB_LINE_PROPERTY_VALUE = 'true';

    /**
     * Main BYOB Collection Controller
     */
    class BYOBCollection {
        constructor() {
            this.slotSwiper = null;
            this.selectedProducts = [];
            this._discountMessageHideTimeout = null;
            this._bundleDiscountMessageShown = false;
            this.init();
        }

        getSectionEl() {
            return document.querySelector('[data-byob-section]') || document.querySelector('.pdm-byob-collection');
        }

        getSlotCarouselEl() {
            return document.querySelector('[data-slot-carousel]');
        }

        getSlotConfig() {
            const el = this.getSlotCarouselEl();
            if (!el?.dataset) return { maxSlots: 10, slidesDesktop: 5, slidesMobile: 4, breakpoint: 768, removeTplId: 'slot-remove-tpl' };
            return {
                maxSlots: parseInt(el.dataset.maxSlots, 10) || 10,
                slidesDesktop: parseInt(el.dataset.slidesDesktop, 10) || 5,
                slidesMobile: parseInt(el.dataset.slidesMobile, 10) || 4,
                breakpoint: parseInt(el.dataset.breakpoint, 10) || 768,
                removeTplId: el.dataset.removeTplId || 'slot-remove-tpl',
            };
        }

        getSectionConfig() {
            const el = this.getSectionEl();
            if (!el?.dataset) return { scrollOffset: 200, tabScrollOffset: 20, resizeDebounce: 150, scrollDebounce: 100 };
            return {
                scrollOffset: parseInt(el.dataset.scrollOffset, 10) || 200,
                tabScrollOffset: parseInt(el.dataset.tabScrollOffset, 10) || 20,
                resizeDebounce: parseInt(el.dataset.resizeDebounce, 10) || 150,
                scrollDebounce: parseInt(el.dataset.scrollDebounce, 10) || 100,
            };
        }

        /**
         * Initialize all components
         */
        init() {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.setup());
            } else {
                this.setup();
            }
        }

        /**
         * Setup all functionality
         */
        setup() {
            this._lastByobModalProductDataset = null;
            this._renderAddMessageBold();
            const section = this.getSectionEl();
            if (section) {
                const obs = new MutationObserver(() => this._renderAddMessageBold());
                obs.observe(section, { childList: true, subtree: true });
            }
            this.initCarousels();
            this.initByobProductCarouselsResize();
            this.initByobSwiperNoSwipe();
            this.initTabNavigation();
            this.initSelectedItems();
            this.initCheckoutBar();
            this.initUiBar();
            this.initSlotArrowsResize();
            this.initByobModalSelect();
            this.initByobDesktopSelect();
            this._hideByobLoading();
        }

        /**
         * Set add message HTML with "successfully added" in bold (same approach as discount).
         */
        _renderAddMessageBold() {
            const section = this.getSectionEl();
            if (!section) return;
            const addTextEl = section.querySelector('.pdm-byob-collection__content-message_add-text');
            if (!addTextEl) return;
            if (addTextEl.querySelector('.pdm-byob-collection__content-message_add-bold')) return;
            const msg = section.dataset.addMessage || addTextEl.textContent || 'Added to bundle';
            const parts = String(msg).trim().split('successfully added');
            if (parts.length === 2) {
                addTextEl.innerHTML = parts[0] + '<span class="pdm-byob-collection__content-message_add-bold">successfully added</span>' + (parts[1] || '');
            }
        }

        _hideByobLoading() {
            const loadingEl = document.getElementById('pdm-byob-loading');
            if (!loadingEl) return;
            const hide = () => {
                loadingEl.setAttribute('aria-hidden', 'true');
            };
            const run = () => requestAnimationFrame(() => setTimeout(hide, 120));
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', run);
            } else {
                run();
            }
        }

        /**
         * Shared: add selected variant to BYOB bar (used by modal and desktop).
         * @param {{ id: number, title?: string, featured_image?: object, variants?: Array, price?: number }} productData - product JSON
         * @param {string} variantId - selected variant id
         * @param {{ image?: { src: string }|string, title?: string, price?: number }} overrides - optional overrides (e.g. from desktop card DOM after color change)
         */
        _addSelectedVariantToBar(productData, variantId, overrides = {}) {
            if (!productData?.id || !variantId) return;
            const variant = (productData.variants || []).find((v) => String(v.id) === String(variantId));
            const image = overrides.image !== undefined ? overrides.image : (variant?.featured_image || productData.featured_image);
            const price = overrides.price !== undefined ? overrides.price : (variant?.price ?? productData.price);
            const title = overrides.title !== undefined ? overrides.title : (productData.title || '');
            const { maxSlots } = this.getSlotConfig();
            if (this.selectedProducts.length >= maxSlots) return;
            this.selectedProducts.push({
                productId: String(productData.id),
                variantId: String(variantId),
                sellingPlanId: undefined,
                image: image,
                price: price,
                title: title,
            });
            this.updateItemCounter();
            this.initUiBar();
            this.updateCardsForProduct(String(productData.id));
            this.initFloatMessage('add');
        }

        initByobDesktopSelect() {
            const section = this.getSectionEl();
            if (!section) return;

            section.addEventListener('submit', (e) => {
                const form = e.target;
                if (!form?.closest?.('.pdm-byob-product-card')) return;
                e.preventDefault();
                e.stopPropagation();
            }, true);

            // On desktop, theme updates the first cart-add input; we need the main input [data-product="input"] in sync
            section.addEventListener('click', (e) => {
                const colorBtn = e.target?.closest?.('.data-colors');
                if (!colorBtn || !colorBtn.closest('.pdm-byob-product-card')) return;
                const item = colorBtn.closest('product-item');
                if (!item) return;
                const mainInput = item.querySelector('[data-product="input"]');
                if (!mainInput) return;
                const sizes = item.querySelectorAll('.data-sizes');
                const underlinedIdx = Array.from(sizes).findIndex((s) => s.classList.contains('underline'));
                const selectedSize = sizes[underlinedIdx];
                if (selectedSize?.dataset?.variantId) {
                    setTimeout(() => {
                        mainInput.value = selectedSize.dataset.variantId;
                    }, 0);
                }
            }, false);

            const runPatch = () => this._patchByobDesktopCards();
            runPatch();
            requestAnimationFrame(() => runPatch());
            setTimeout(runPatch, 300);
            const obs = new MutationObserver(() => runPatch());
            obs.observe(section, { childList: true, subtree: true });
        }

        /**
         * In the modal, each color is a different product; the input holds the selected variant id.
         * Find the .data-colors button whose data-color-variant-ids contains this variantId and return
         * that product id + overrides (image, title, price). Same logic as rest of site: input = source of truth.
         */
        _getModalColorDataFromVariantId(modalContent, variantId) {
            if (!modalContent || !variantId) return null;
            const colorBtns = modalContent.querySelectorAll('.data-colors');
            for (const btn of colorBtns) {
                let ids = [];
                try {
                    const raw = btn.dataset.colorVariantIds || btn.getAttribute('data-color-variant-ids');
                    ids = raw ? JSON.parse(raw) : [];
                } catch (_) { /* ignore */ }
                if (!ids.some((id) => String(id) === String(variantId))) continue;
                const productId = btn.dataset.productId || btn.getAttribute('data-product-id');
                if (!productId) return null;
                let imgUrl = btn.dataset.colorVariantImage || btn.getAttribute('data-color-variant-image') || '';
                if (!imgUrl && this._lastByobModalProductDataset?.relatedProducts) {
                    try {
                        const related = JSON.parse(this._lastByobModalProductDataset.relatedProducts);
                        const arr = Array.isArray(related) ? related : (related?.value || []);
                        const relatedProduct = arr.find((p) => String(p?.id) === String(productId));
                        if (relatedProduct) {
                            const img = relatedProduct.featured_image?.src || relatedProduct.featured_image?.url
                                || (typeof relatedProduct.featured_image === 'string' ? relatedProduct.featured_image : null)
                                || relatedProduct.images?.[0]?.src || relatedProduct.images?.[0]?.url;
                            if (img) imgUrl = img;
                        }
                    } catch (_) { /* ignore */ }
                }
                const title = btn.dataset.colorVariantTitle || btn.getAttribute('data-color-variant-title') || '';
                const priceStr = btn.dataset.colorPrice || btn.getAttribute('data-color-price') || '';
                const parsed = parseFloat(String(priceStr).replace(/[^0-9.]/g, ''));
                const price = Number.isNaN(parsed) ? undefined : Math.round(parsed * 100);
                return { productId: String(productId), image: imgUrl ? { src: String(imgUrl) } : undefined, title, price };
            }
            return null;
        }

        /**
         * Read current image, title and price from desktop card DOM (so color change is reflected when adding to bar).
         */
        _getDesktopCardOverrides(productItem) {
            const overrides = {};
            const imgEl = productItem.querySelector('[data-product="image-1"]');
            const img = imgEl?.tagName?.toLowerCase() === 'img' ? imgEl : imgEl?.querySelector?.('img');
            if (img?.src) {
                overrides.image = { src: img.currentSrc || img.src };
            }
            const titleEl = productItem.querySelector('[data-product="title"]');
            if (titleEl?.textContent) {
                overrides.title = titleEl.textContent.trim();
            }
            const priceEl = productItem.querySelector('[data-product="price"]');
            if (priceEl?.textContent) {
                const parsed = parseFloat(priceEl.textContent.replace(/[^0-9.]/g, ''));
                if (!Number.isNaN(parsed)) {
                    overrides.price = Math.round(parsed * 100);
                }
            }
            return overrides;
        }

        _patchByobDesktopCards() {
            const section = this.getSectionEl();
            if (!section) return;
            const cards = section.querySelectorAll('.pdm-byob-product-card product-item');
            cards.forEach((productItem) => {
                const mainInput = productItem.querySelector('[data-product="input"]');
                const atcForm = mainInput?.closest('form');
                if (!atcForm) return;
                // Button has no type in HTML (defaults to submit); match by class or first enabled button
                const atcBtn = atcForm.querySelector('button.button--black:not([disabled])') || atcForm.querySelector('button:not([disabled])');
                if (!atcBtn || atcBtn.dataset.byobDesktopPatched === 'true') return;

                atcBtn.dataset.byobDesktopPatched = 'true';
                atcBtn.textContent = 'Select';
                atcBtn.type = 'button';

                atcBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const variantId = mainInput?.value;
                    if (!variantId) return;
                    let productData = null;
                    try {
                        productData = JSON.parse(productItem.dataset.modalData || '{}');
                    } catch (_) { /* ignore */ }
                    if (!productData?.id) return;
                    // Resolve product from variant (same as modal: input = selected variant; user may have changed color)
                    const colorData = this._getModalColorDataFromVariantId(productItem, variantId);
                    if (colorData) {
                        productData = { ...productData, id: colorData.productId };
                        this._addSelectedVariantToBar(productData, variantId, {
                            image: colorData.image,
                            title: colorData.title,
                            price: colorData.price,
                        });
                    } else {
                        this._addSelectedVariantToBar(productData, variantId, this._getDesktopCardOverrides(productItem));
                    }
                });
            });
        }

        initByobModalSelect() {
            const section = this.getSectionEl();
            if (!section) return;

            document.addEventListener('click', (e) => {
                const btn = e.target?.closest?.('button');
                if (!btn?.matches?.('[data-modal-trigger^="color-add-"]')) return;
                if (!btn.closest('[data-byob-section]')) return;
                const productItem = btn.closest('product-item');
                if (productItem) {
                    this._lastByobModalProductDataset = { ...productItem.dataset };
                }
            }, true);

            const container = document.getElementById('quick-buy-modal-container');
            if (!container) return;

            const runPatch = () => {
                if (!this._lastByobModalProductDataset || !document.querySelector('[data-byob-section]')) return;
                const content = container.querySelector('.product-small-details.active');
                if (!content) return;
                const atcBtn = content.querySelector('.add-to-cart-js');
                if (!atcBtn || atcBtn.dataset.byobSelectPatched === 'true') return;

                atcBtn.dataset.byobSelectPatched = 'true';
                atcBtn.textContent = 'Select';
                atcBtn.type = 'button';

                atcBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const form = content.querySelector('cart-add form');
                    const input = form?.querySelector('input[name="id"]');
                    const variantId = input?.value?.trim();
                    if (!variantId) return;
                    let productData = null;
                    try {
                        productData = JSON.parse(this._lastByobModalProductDataset.modalData || '{}');
                    } catch (_) { /* ignore */ }
                    if (!productData?.id) return;
                    // Resolve product from variant (same as rest of site: input = selected variant; color may have changed)
                    const colorData = this._getModalColorDataFromVariantId(content, variantId);
                    if (colorData) {
                        productData = { ...productData, id: colorData.productId };
                        this._addSelectedVariantToBar(productData, variantId, {
                            image: colorData.image,
                            title: colorData.title,
                            price: colorData.price,
                        });
                    } else {
                        this._addSelectedVariantToBar(productData, variantId, {});
                    }
                    this._byobCloseQuickBuyModal();
                    this._lastByobModalProductDataset = null;
                }, { once: true });
            };

            const obs = new MutationObserver(() => {
                if (container.classList.contains('hidden')) return;
                const content = container.querySelector('.product-small-details.active');
                if (content) setTimeout(runPatch, 0);
            });
            obs.observe(container, { attributes: true, attributeFilter: ['class'], subtree: true });
        }

        _byobCloseQuickBuyModal() {
            const container = document.getElementById('quick-buy-modal-container');
            const content = container?.querySelector('.product-small-details');
            if (!content) return;
            content.classList.remove('active');
            document.body.classList.remove('overflow-hidden');
            container?.classList.remove('quick-buy-byob');
            if (container) setTimeout(() => container.classList.add('hidden'), 300);
        }

        /**
         * Sample the image background from the four corners and average, so left/right stay even. Returns rgb(r,g,b) or null if CORS blocks.
         */
        _getImageBackgroundColor(img) {
            try {
                const canvas = document.createElement('canvas');
                const w = Math.min(img.naturalWidth || img.width, 64);
                const h = Math.min(img.naturalHeight || img.height, 64);
                if (!w || !h) return null;
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                if (!ctx) return null;
                ctx.drawImage(img, 0, 0, w, h);
                const full = ctx.getImageData(0, 0, w, h);
                const data = full.data;
                const sample = (x, y) => {
                    const i = (y * w + x) * 4;
                    return { r: data[i], g: data[i + 1], b: data[i + 2] };
                };
                const size = 3;
                const cornerRanges = [
                    { x0: 0, x1: Math.min(size, w), y0: 0, y1: Math.min(size, h) },
                    { x0: Math.max(0, w - size), x1: w, y0: 0, y1: Math.min(size, h) },
                    { x0: 0, x1: Math.min(size, w), y0: Math.max(0, h - size), y1: h },
                    { x0: Math.max(0, w - size), x1: w, y0: Math.max(0, h - size), y1: h },
                ];
                let r = 0, g = 0, b = 0, n = 0;
                cornerRanges.forEach(({ x0, x1, y0, y1 }) => {
                    for (let y = y0; y < y1; y++) {
                        for (let x = x0; x < x1; x++) {
                            const p = sample(x, y);
                            r += p.r;
                            g += p.g;
                            b += p.b;
                            n++;
                        }
                    }
                });
                if (n === 0) return null;
                r = Math.round(r / n);
                g = Math.round(g / n);
                b = Math.round(b / n);
                return `rgb(${r},${g},${b})`;
            } catch (_) {
                return null;
            }
        }

        /**
         * Safari: refresh Swiper for BYOB product carousels only (inside [data-byob-section]).
         */
        initByobProductCarouselsResize() {
            const section = this.getSectionEl();
            if (!section) return;
            const { resizeDebounce } = this.getSectionConfig();
            let resizeT;
            const run = () => {
                section.querySelectorAll('site-carousel').forEach((el) => {
                    const inst = el.swiper;
                    if (inst && typeof inst.update === 'function') {
                        try {
                            inst.update();
                        } catch (_) {}
                    }
                });
            };
            const onResize = () => {
                clearTimeout(resizeT);
                resizeT = setTimeout(run, resizeDebounce);
            };
            window.addEventListener('resize', onResize, { passive: true });
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', onResize, { passive: true });
            }
        }

        /**
         * Update slot arrows visibility on resize (desktop 5 / mobile 4).
         */
        initSlotArrowsResize() {
            let resizeT;
            const { resizeDebounce } = this.getSectionConfig();
            window.addEventListener('resize', () => {
                clearTimeout(resizeT);
                resizeT = setTimeout(() => {
                    this.updateSlotArrowsVisibility();
                    if (this.slotSwiper && typeof this.slotSwiper.update === 'function') {
                        try {
                            this.slotSwiper.update();
                        } catch (_) {}
                    }
                }, resizeDebounce);
            });
        }

        /**
         * Initialize all Swiper carousels: product collections + slot carousel.
         * Product carousels: 2 slides mobile, 4 desktop. Slot carousel: 4 mobile, 5 desktop, left-aligned.
         */
        initCarousels() {
            if (typeof Swiper === 'undefined') return;

            // Los carruseles de productos ahora los inicializa el componente `site-carousel` del tema.
            // Aquí solo inicializamos el carrusel de slots inferior.
            this.initSlotSwiper();
        }

        /**
         * Swiper no debe capturar el gesto horizontal en el panel de tallas/colores (clics mal leídos como swipe).
         * Con `noSwiping: true` en data-config del site-carousel, marcamos el panel blanco inferior y controles BYOB.
         * @see https://swiperjs.com/swiper-api#param-noSwiping
         */
        initByobSwiperNoSwipe() {
            const section = this.getSectionEl();
            if (!section) return;

            const mark = () => {
                section.querySelectorAll('.pdm-byob-product-card product-item > div.relative.pb-20 > div.bg-white.border-t').forEach((el) => {
                    el.classList.add('swiper-no-swiping');
                });
                section.querySelectorAll('.pdm-byob-product-card .pdm-byob-product-card__controls').forEach((el) => {
                    el.classList.add('swiper-no-swiping');
                });
            };

            mark();
            const obs = new MutationObserver(() => mark());
            obs.observe(section, { childList: true, subtree: true });
        }

        /**
         * Initialize Swiper for selected-items slot carousel.
         * 4 slides visible on mobile, 5 on desktop; left-aligned; prev/next arrows.
         */
        initSlotSwiper() {
            const wrap = this.getSlotCarouselEl();
            const slotEl = wrap?.querySelector('.pdm-byob-collection__slot-swiper');
            const prev = wrap?.querySelector('.pdm-byob-collection__slot-arrow--prev');
            const next = wrap?.querySelector('.pdm-byob-collection__slot-arrow--next');
            if (!slotEl || !prev || !next) return;

            const { slidesMobile, slidesDesktop, breakpoint } = this.getSlotConfig();

            try {
                this.slotSwiper = new Swiper(slotEl, {
                    slidesPerView: slidesMobile,
                    spaceBetween: 8,
                    slidesOffsetBefore: 0,
                    slidesOffsetAfter: 0,
                    breakpoints: {
                        [breakpoint]: { slidesPerView: slidesDesktop },
                    },
                    navigation: {
                        prevEl: prev,
                        nextEl: next,
                    },
                });
            } catch (error) {
                console.error('Error initializing slot Swiper:', error);
            }
        }

        /**
         * Initialize tab navigation with active state management
         */
        initTabNavigation() {
            const tabs = document.querySelectorAll('.pdm-byob-collection__tab');
            const sections = document.querySelectorAll('.pdm-byob-collection__carousel-wrapper');

            if (tabs.length === 0 || sections.length === 0) return;

            // Handle tab clicks
            tabs.forEach((tab) => {
                tab.addEventListener('click', (e) => this.handleTabClick(e, tab));
            });

            // Update active tab on scroll
            this.updateActiveTabOnScroll(tabs, sections);

            // Update active tab on page load
            this.updateActiveTab(tabs, sections);

            // Update active tab when hash changes
            window.addEventListener('hashchange', () => this.updateActiveTab(tabs, sections));
        }

        /**
         * Handle tab click: scroll so the section title is visible just below the fixed/sticky bar (header + tabs).
         * Desktop y mobile usan --header-height / --header-height-mobile y un offset mínimo.
         */
        handleTabClick(e, tab) {
            const targetId = tab.getAttribute('href')?.substring(1);
            const tabsNav = document.querySelector('.pdm-byob-collection__tabs');

            if (!targetId) return;

            const targetSection = document.getElementById(targetId);
            if (!targetSection) return;

            e.preventDefault();

            const titleEl = targetSection.querySelector('.pdm-byob-collection__title');
            const scrollTarget = titleEl || targetSection;
            const elementPosition = scrollTarget.getBoundingClientRect().top;

            const { tabScrollOffset } = this.getSectionConfig();
            const { breakpoint } = this.getSlotConfig();
            const isMobile = window.innerWidth < breakpoint;

            const headerVar = isMobile ? '--header-height-mobile' : '--header-height';
            const headerFallback = isMobile ? 86 : 110;
            const headerPx = parseFloat(
                getComputedStyle(document.documentElement).getPropertyValue(headerVar)?.trim() || String(headerFallback)
            );
            const tabsHeight = tabsNav?.offsetHeight ?? 56;
            /* Mobile: solo header + tabs + 12px para que el título quede más cerca de la barra y se note el ajuste */
            const extra = isMobile ? 12 : tabScrollOffset + 20;
            const offset = Math.max(isMobile ? 140 : 150, headerPx + tabsHeight + extra);

            const offsetPosition = elementPosition + window.scrollY - offset;

            window.scrollTo({
                top: Math.max(0, offsetPosition),
                behavior: 'smooth'
            });
            window.history.pushState(null, null, `#${targetId}`);

            const tabs = document.querySelectorAll('.pdm-byob-collection__tab');
            const sections = document.querySelectorAll('.pdm-byob-collection__carousel-wrapper');
            this.updateActiveTab(tabs, sections);
        }

        /**
         * Update active tab based on scroll position
         */
        updateActiveTabOnScroll(tabs, sections) {
            let scrollTimeout;
            const { scrollDebounce } = this.getSectionConfig();
            window.addEventListener('scroll', () => {
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    this.updateActiveTab(tabs, sections);
                }, scrollDebounce);
            }, { passive: true });
        }

        /**
         * Update active tab state.
         * On mobile uses a smaller offset so the active tab stays on the current category
         * when a bit of the next section's title is visible (avoids jumping to next tab).
         */
        updateActiveTab(tabs, sections) {
            const { scrollOffset } = this.getSectionConfig();
            const { breakpoint } = this.getSlotConfig();
            const isMobile = typeof window !== 'undefined' && window.innerWidth < breakpoint;
            const effectiveOffset = isMobile ? Math.min(80, scrollOffset) : scrollOffset;
            const scrollPosition = window.scrollY + effectiveOffset;
            let activeSection = null;

            sections.forEach((section) => {
                const sectionTop = section.offsetTop;
                const sectionBottom = sectionTop + section.offsetHeight;

                if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                    activeSection = section.id;
                }
            });

            // If no section is in view, check URL hash
            if (!activeSection && window.location.hash) {
                activeSection = window.location.hash.substring(1);
            }

            tabs.forEach((tab) => {
                const collectionId = tab.getAttribute('data-collection-id');
                if (collectionId === activeSection) {
                    tab.classList.add('pdm-byob-collection__tab--active');
                } else {
                    tab.classList.remove('pdm-byob-collection__tab--active');
                }
            });
        }

        initFloatMessage(action) {
            const containerMessageAdd = document.querySelector('.pdm-byob-collection__content-message_add');
            let incrementCounterId;

            if (action === 'add') {
                countTimeout = 0;
                containerMessageAdd.classList.add('active');
                if (timerGlobal > 0) clearInterval(idInterval);

                function incrementCounter() {
                    idInterval = incrementCounterId;
                    if (countTimeout >= 5) {
                        timerGlobal = 0;
                        containerMessageAdd.classList.remove('active');
                        clearInterval(idInterval);
                    } else {
                        countTimeout++;
                        timerGlobal = countTimeout;
                    }
                }
                if (containerMessageAdd.classList.contains('active')) {
                    incrementCounterId = setInterval(incrementCounter, 1000);
                }
            } else if (action === 'remove') {
                containerMessageAdd.classList.remove('active');
            }
        }

        /**
         * Update the bar counter (total selected items).
         */
        updateItemCounter() {
            const el = document.querySelector('.pdm-byob-collection__item-counter');
            if (el) el.textContent = String(this.selectedProducts.length);
            this.updateDiscountMessage();
        }

        /**
         * Show discount message when reaching the configured item threshold (default: 3 items).
         * Resets when count goes below threshold or when the bundle is cleared (Add to Cart).
         */
        updateDiscountMessage() {
            const msgEl = document.getElementById('pdm-byob-discount-message');
            if (!msgEl) return;
            const section = this.getSectionEl();
            if (!section) return;

            if (this._discountMessageHideTimeout) {
                clearTimeout(this._discountMessageHideTimeout);
                this._discountMessageHideTimeout = null;
            }

            const threshold = parseInt(section.dataset.discountThreshold, 10);
            const itemThreshold = Number.isFinite(threshold) && threshold > 0 ? threshold : 3;
            const count = this.selectedProducts.length;
            if (count < itemThreshold) {
                this._bundleDiscountMessageShown = false;
            }

            const discountMsg = section.dataset.discountMessage || '';

            let text = '';
            if (count === itemThreshold && discountMsg && !this._bundleDiscountMessageShown) {
                text = discountMsg;
                this._bundleDiscountMessageShown = true;
            }

            const textEl = msgEl.querySelector('.pdm-byob-collection__content-message_discount-text') || msgEl;
            if (text) {
                const parts = text.trim().split(/\s+/);
                const strongPart = parts[0] || '';
                const restPart = parts.slice(1).join(' ');
                textEl.innerHTML = strongPart ? `<strong>${strongPart}</strong>${restPart ? ` ${restPart}` : ''}` : text;
            } else {
                textEl.textContent = '';
            }

            if (text) {
                msgEl.hidden = false;
                msgEl.classList.add('active');
                this._discountMessageHideTimeout = setTimeout(() => {
                    msgEl.classList.remove('active');
                    setTimeout(() => {
                        msgEl.hidden = true;
                        this._discountMessageHideTimeout = null;
                    }, 300);
                }, 5000);
            } else {
                msgEl.classList.remove('active');
                setTimeout(() => { msgEl.hidden = true; }, 300);
            }
        }

        /**
         * Initialize selected items management (add/remove from cards and slot X).
         */
        initSelectedItems() {
            document.addEventListener('click', (e) => {
                const addBtn = e.target.closest('.pdm-byob-product-card__add');
                const removeBtn = e.target.closest('.pdm-byob-product-card__remove');
                const slotRemoveBtn = e.target.closest('.pdm-byob-collection__slot-remove');

                if (addBtn) {
                    this.handleAdd(addBtn);
                    this.updateItemCounter();
                    this.initFloatMessage("add");
                } else if (removeBtn) {
                    this.handleRemove(removeBtn);
                    this.updateItemCounter();
                    this.initFloatMessage("remove");
                } else if (slotRemoveBtn) {
                    const idx = slotRemoveBtn.getAttribute('data-absolute-index');
                    if (idx !== null) {
                        this.handleRemoveBySlotIndex(parseInt(idx, 10));
                        this.updateItemCounter();
                    }
                }
            });
        }

        /**
         * Add one unit of product to selection (card + button).
         */
        handleAdd(btn) {
            const { maxSlots } = this.getSlotConfig();
            if (this.selectedProducts.length >= maxSlots) return;

            const controls = btn.closest('.pdm-byob-product-card__controls');
            const card = btn.closest('.pdm-byob-product-card');
            if (!controls || !card) return;

            const productId = card.dataset.productId;
            const sellingPlanId = card.dataset.defaultSellingPlanId ? parseInt(card.dataset.defaultSellingPlanId, 10) : null;
            const variantJsonScript = controls.querySelector('script[type="application/json"]');
            let dataProduct = null;
            try {
                dataProduct = variantJsonScript ? JSON.parse(variantJsonScript.textContent) : null;
            } catch (_) { /* ignore */ }
            if (!dataProduct?.variants?.length) return;

            this.selectedProducts.push({
                productId: productId,
                variantId: dataProduct.variants[0].id,
                sellingPlanId: sellingPlanId || undefined,
                image: dataProduct.featured_image,
                price: dataProduct.price,
                title: dataProduct.title,
            });
            if (controls.dataset.active === 'false') {
                controls.dataset.active = 'true';
                controls.classList.add('pdm-byob-product-card__controls--active');
            }

            const quantityEl = controls.querySelector('.pdm-byob-product-card__quantity');
            const currentQty = this.selectedProducts.filter((p) => p.productId === productId).length;
            quantityEl.textContent = currentQty;

            this.initUiBar();
        }

        /**
         * Remove one unit of product from selection (card minus button).
         */
        handleRemove(btn) {
            const controls = btn.closest('.pdm-byob-product-card__controls');
            const card = btn.closest('.pdm-byob-product-card');
            if (!controls || !card) return;

            const productId = card.dataset.productId;
            const lastIndex = this.selectedProducts.findLastIndex((p) => p.productId === productId);
            if (lastIndex === -1) return;

            this.selectedProducts.splice(lastIndex, 1);
            this.updateCardsForProduct(productId);
            this.initUiBar();
        }

        /**
         * Remove product at slot index (X click) and sync quantity on cards.
         */
        handleRemoveBySlotIndex(absoluteIndex) {
            if (absoluteIndex < 0 || absoluteIndex >= this.selectedProducts.length) return;
            const product = this.selectedProducts[absoluteIndex];
            const productId = product?.productId;
            if (!productId) return;

            this.selectedProducts.splice(absoluteIndex, 1);
            this.updateCardsForProduct(productId);
            this.initUiBar();
        }

        /**
         * Update quantity and active state on all cards for the product.
         */
        updateCardsForProduct(productId) {
            const cards = document.querySelectorAll(`.pdm-byob-product-card[data-product-id="${productId}"]`);
            const currentQty = this.selectedProducts.filter((p) => p.productId === productId).length;

            cards.forEach((card) => {
                const controls = card.querySelector('.pdm-byob-product-card__controls');
                const quantityEl = controls?.querySelector('.pdm-byob-product-card__quantity');
                if (quantityEl) quantityEl.textContent = currentQty;
                if (!controls) return;
                if (currentQty === 0) {
                    controls.dataset.active = 'false';
                    controls.classList.remove('pdm-byob-product-card__controls--active');
                }
            });
        }

        /**
         * Get current slidesPerView for slot carousel (5 desktop, 4 mobile).
         */
        getSlotSlidesPerView() {
            const { breakpoint, slidesDesktop, slidesMobile } = this.getSlotConfig();
            return typeof window !== 'undefined' && window.matchMedia(`(min-width: ${breakpoint}px)`).matches ? slidesDesktop : slidesMobile;
        }

        /**
         * Update slot arrows disabled state. Always visible and reserve space.
         * If total ≤ 5 (desktop) or ≤ 4 (mobile): both disabled. Otherwise enabled and Swiper controls prev/next at bounds.
         */
        updateSlotArrowsVisibility() {
            const total = this.selectedProducts.length;
            const wrap = this.getSlotCarouselEl();
            const prev = wrap?.querySelector('.pdm-byob-collection__slot-arrow--prev');
            const next = wrap?.querySelector('.pdm-byob-collection__slot-arrow--next');
            const canNavigate = total > this.getSlotSlidesPerView();
            if (prev) prev.disabled = !canNavigate;
            if (next) next.disabled = !canNavigate;
            if (!canNavigate && this.slotSwiper) {
                // Si ya no hay suficientes items para navegar, vuelve siempre al inicio
                // para que el usuario no quede "atascado" en el último grupo visible.
                this.slotSwiper.slideTo(0, 0);
                this.slotSwiper.allowTouchMove = false;
            } else if (this.slotSwiper) {
                this.slotSwiper.allowTouchMove = true;
            }
        }

        /**
         * Reset slot carousel after add-to-cart: update Swiper, go to first slide, refresh arrows.
         */
        resetSlotCarousel() {
            if (!this.slotSwiper) return;
            this.slotSwiper.update();
            this.slotSwiper.slideTo(0, 0);
            this.updateSlotArrowsVisibility();
        }

        /**
         * Update the 10 slot drawers (already in HTML). Filled = image + X; empty = number.
         * Arrows shown when more than 5 items (desktop) or 4 (mobile).
         */
        initUiBar() {
            this.updateSlotArrowsVisibility();

            const wrap = this.getSlotCarouselEl();
            const slots = wrap?.querySelectorAll('.pdm-byob-collection__slot-swiper .swiper-slide') || [];
            if (slots.length === 0) {
                this.updateAddToCartButtons();
                return;
            }

            const { maxSlots, removeTplId } = this.getSlotConfig();
            const removeTpl = document.getElementById(removeTplId);
            const limit = Math.min(maxSlots, slots.length);

            for (let i = 0; i < limit; i++) {
                const slot = slots[i];
                const product = this.selectedProducts[i];
                const numEl = slot.querySelector('.pdm-byob-collection__slot-number');
                const contentEl = slot.querySelector('.pdm-byob-collection__slot-content');

                if (product) {
                    const imgUrl = product.image && (typeof product.image === 'string' ? product.image : (product.image.src || product.image.url));
                    slot.classList.add('pdm-byob-collection__item-slot--filled');
                    slot.style.backgroundImage = '';
                    if (numEl) numEl.textContent = '';
                    if (contentEl && removeTpl) {
                        contentEl.innerHTML = '';
                        contentEl.style.backgroundColor = '';
                        if (imgUrl) {
                            const img = document.createElement('img');
                            img.crossOrigin = 'anonymous';
                            img.alt = product.title || '';
                            img.loading = 'lazy';
                            img.className = 'pdm-byob-collection__slot-img';
                            img.addEventListener('load', () => {
                                const bg = this._getImageBackgroundColor(img);
                                contentEl.style.backgroundColor = bg || '#EFEFED';
                            });
                            img.src = imgUrl;
                            contentEl.appendChild(img);
                        }
                        const btn = removeTpl.content.cloneNode(true).querySelector('button');
                        btn.setAttribute('data-absolute-index', String(i));
                        contentEl.appendChild(btn);
                    }
                } else {
                    slot.classList.remove('pdm-byob-collection__item-slot--filled');
                    slot.style.backgroundImage = '';
                    if (numEl) numEl.textContent = String(i + 1);
                    if (contentEl) {
                        contentEl.innerHTML = '';
                        contentEl.style.backgroundColor = '';
                        contentEl.style.backgroundImage = '';
                    }
                }
            }

            this.updateAddToCartButtons();
        }

        /**
         * Enable/disable Add to Cart buttons based on selection.
         */
        updateAddToCartButtons() {
            const btns = document.querySelectorAll('.pdm-byob-collection__add-to-cart');
            const disabled = this.selectedProducts.length === 0;
            btns.forEach((btn) => { btn.disabled = disabled; });
        }

        /**
         * Initialize checkout bar interactions (Add to Cart).
         */
        initCheckoutBar() {
            document.addEventListener('click', (e) => {
                const btn = e.target.closest('.pdm-byob-collection__add-to-cart');
                if (!btn || btn.disabled) return;
                e.preventDefault();
                this.handleAddToCart();
            });
        }

        /**
         * Add BYOB selection to cart, open drawer, then reset selection.
         * Shows loading state, error message on failure, and restores UI in finally.
         */
        async handleAddToCart() {
            if (this.selectedProducts.length === 0) return;

            const btns = document.querySelectorAll('.pdm-byob-collection__add-to-cart');
            const errorEl = document.getElementById('pdm-byob-atc-error');
            const defaultErrorText = 'Something went wrong. Please try again.';

            const setButtonText = (text) => {
                btns.forEach((b) => { b.textContent = text; });
            };
            const restoreButtonText = () => {
                btns.forEach((b) => {
                    const t = b.dataset.atcText;
                    if (t) b.textContent = t;
                });
            };
            const showError = (message) => {
                if (errorEl) {
                    errorEl.textContent = message || defaultErrorText;
                    errorEl.hidden = false;
                }
                btns.forEach((b) => b.classList.add('pdm-byob-collection__add-to-cart--error'));
            };
            const clearError = () => {
                if (errorEl) {
                    errorEl.textContent = '';
                    errorEl.hidden = true;
                }
                btns.forEach((b) => b.classList.remove('pdm-byob-collection__add-to-cart--error'));
            };

            clearError();
            btns.forEach((b) => {
                b.disabled = true;
                b.classList.add('pdm-byob-collection__add-to-cart--loading');
                b.setAttribute('aria-busy', 'true');
            });
            setButtonText('Adding...');

            const items = this.selectedProducts.map((p) => {
                const variantId = Number(p.variantId) || p.variantId;
                const item = {
                    id: variantId,
                    quantity: 1,
                    properties: {
                        [BYOB_LINE_PROPERTY_KEY]: BYOB_LINE_PROPERTY_VALUE,
                    },
                };
                if (p.sellingPlanId) item.selling_plan = p.sellingPlanId;
                return item;
            });
            const productIds = [...new Set(this.selectedProducts.map((p) => p.productId))];
            const siteCart = document.querySelector('site-cart');

            try {
                if (siteCart && typeof siteCart.getCartSections === 'function') {
                    const sections = siteCart.getCartSections().map((s) => s.section);
                    const root = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '';
                    const addUrl = (root.replace(/\/$/, '') || '') + '/cart/add';
                    const addRes = await fetch(addUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                        body: JSON.stringify({ items, sections }),
                    });
                    const data = await addRes.json().catch(() => ({}));
                    if (data.status || !addRes.ok) {
                        throw new Error(data.description || data.message || defaultErrorText);
                    }
                    if (data.sections && typeof siteCart.renderCartSections === 'function') {
                        siteCart.renderCartSections(data.sections);
                    }
                    if (typeof siteCart.handleRewards === 'function') await siteCart.handleRewards();
                    if (typeof siteCart.cartCountUpdate === 'function') siteCart.cartCountUpdate();
                    if (typeof siteCart.openCart === 'function') siteCart.openCart(false);
                    if (typeof siteCart.setupCartFocus === 'function') {
                        setTimeout(() => siteCart.setupCartFocus(), 100);
                    }
                } else {
                    const root = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
                    const addRes = await fetch(root.replace(/\/$/, '') + '/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items }),
                    });
                    const data = await addRes.json().catch(() => ({}));
                    if (!addRes.ok) {
                        throw new Error(data.description || data.message || defaultErrorText);
                    }
                    const cartRes = await fetch(root.replace(/\/$/, '') + '/cart.js');
                    const cart = await cartRes.json();
                    if (typeof updateCartDrawer === 'function') await updateCartDrawer();
                    if (typeof updateBubbleCartCount === 'function') updateBubbleCartCount(cart.item_count);
                    if (typeof openCartDrawer === 'function') openCartDrawer();
                }

                this.selectedProducts = [];
                this._bundleDiscountMessageShown = false;
                productIds.forEach((id) => this.updateCardsForProduct(id));
                this.initUiBar();
                this.updateItemCounter();
                setTimeout(() => {
                    this.resetSlotCarousel();
                }, 0);
            } catch (err) {
                console.error('BYOB add to cart error:', err);
                showError(err.message || defaultErrorText);
                setTimeout(clearError, 5000);
            } finally {
                restoreButtonText();
                btns.forEach((b) => {
                    b.disabled = this.selectedProducts.length === 0;
                    b.classList.remove('pdm-byob-collection__add-to-cart--loading');
                    b.removeAttribute('aria-busy');
                });
            }
        }
    }

    // Initialize BYOB Collection when DOM is ready
    const byobCollection = new BYOBCollection();

    // Expose to global scope if needed for debugging or external access
    if (typeof window !== 'undefined') {
        window.BYOBCollection = byobCollection;
    }

})();
